import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Navbar as RSNavbar,
  NavbarBrand,
  Nav,
  NavItem,
  NavLink,
  Container,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Button,
} from "reactstrap";
import "./Navbar.css";

const PRODUCT_LINKS = [
  { label: "Baremetal Servers", hash: "#hn-plans" },
  { label: "Virtual Servers", hash: "#hn-plans-cloud" },
  { label: "DevOps Services", hash: "#hn-plans-devop" },
  { label: "Remote Desktop", hash: "#hn-plans-remote" },
];

const Navbar = () => {
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const toggleOffcanvas = () => setOffcanvasOpen(!offcanvasOpen);
  const closeOffcanvas = () => setOffcanvasOpen(false);
  const navigate = useNavigate();

  const isLoggedIn =
    typeof window !== "undefined" && !!localStorage.getItem("hosting-noodle-token");

  const token =
    typeof window !== "undefined" ? localStorage.getItem("hosting-noodle-token") : null;

  const decodeJwtPayload = (jwt) => {
    try {
      const parts = String(jwt || "").split(".");
      if (parts.length < 2) return null;
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  };

  const userProfile = isLoggedIn ? decodeJwtPayload(token) : null;
  const userId =
    userProfile?.id ?? userProfile?.userID ?? userProfile?.userId ?? null;
  const firstName = userProfile?.firstName ?? userProfile?.first_name ?? "";
  const lastName = userProfile?.lastName ?? userProfile?.last_name ?? "";
  const email = userProfile?.email ?? "";
  const displayName =
    `${String(firstName || "").trim()} ${String(lastName || "").trim()}`.trim() ||
    (email ? String(email).split("@")[0] : "");

  const handleLogout = () => {
    try {
      localStorage.removeItem("hosting-noodle-token");
    } catch {
      // ignore
    }
    closeOffcanvas();
    navigate("/");
    window.location.reload();
  };

  const handleProductClick = (hash, e) => {
    closeOffcanvas();
    if (window.location.pathname === "/") {
      e?.preventDefault();
      const id = hash.slice(1);
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      if (window.history.replaceState) {
        window.history.replaceState(null, "", "/" + hash);
      }
    } else {
      navigate("/" + hash);
    }
  };

  const navProductLinks = () =>
    PRODUCT_LINKS.map(({ label, hash }) => (
      <NavItem key={hash}>
        <NavLink
          tag={Link}
          to={"/" + hash}
          onClick={(e) => {
            closeOffcanvas();
            handleProductClick(hash, e);
          }}
          className="hn-offcanvas-product-link"
        >
          {label}
        </NavLink>
      </NavItem>
    ));

  return (
    <>
      <RSNavbar color="dark" dark expand="md" fixed="top" className="hn-navbar">
        <Container>
          <NavbarBrand tag={Link} to="/" className="hn-brand">
            <i className="fas fa-bowl-food me-2" aria-hidden="true" />
            Hosting Noodle
          </NavbarBrand>
          {/* Desktop nav: visible from md up */}
          <Nav className="hn-nav-desktop d-none d-md-flex ms-auto" navbar>
            <NavItem>
              <NavLink tag={Link} to="/">
                Home
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink tag={Link} to="/pricing">
                Pricing
              </NavLink>
            </NavItem>
            <UncontrolledDropdown nav inNavbar>
              <DropdownToggle nav caret className="hn-nav-link">
                Products
              </DropdownToggle>
              <DropdownMenu end>
                {PRODUCT_LINKS.map(({ label, hash }) => (
                  <DropdownItem
                    key={hash}
                    tag={Link}
                    to={"/" + hash}
                    onClick={(e) => handleProductClick(hash, e)}
                  >
                    {label}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </UncontrolledDropdown>
            <NavItem>
              {isLoggedIn ? (
                <NavLink tag={Link} to="/dashboard">
                  Dashboard
                </NavLink>
              ) : (
                <NavLink tag={Link} to="/login">
                  Login
                </NavLink>
              )}
            </NavItem>
            {isLoggedIn && (
              <UncontrolledDropdown nav inNavbar className="hn-user-dropdown">
                <DropdownToggle nav caret className="hn-user-toggle">
                  {displayName}
                  {userId != null ? ` - ID-${userId}` : ""}
                </DropdownToggle>
                <DropdownMenu end>
                  <DropdownItem header>Email: {email || "—"}</DropdownItem>
                  <DropdownItem header>Name: {displayName || "—"}</DropdownItem>
                  <DropdownItem header>ID: {userId != null ? `ID-${userId}` : "—"}</DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem onClick={handleLogout}>Logout</DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </Nav>
          {/* Mobile: hamburger opens offcanvas */}
          <Button
            color="link"
            className="hn-offcanvas-toggler d-md-none"
            onClick={toggleOffcanvas}
            aria-label="Open menu"
          >
            <i className="fas fa-bars" aria-hidden="true" />
          </Button>
        </Container>
      </RSNavbar>

      <Offcanvas
        isOpen={offcanvasOpen}
        toggle={toggleOffcanvas}
        direction="end"
        className="hn-offcanvas"
        scrollable
      >
        <OffcanvasHeader toggle={toggleOffcanvas} className="hn-offcanvas-header">
          Menu
        </OffcanvasHeader>
        <OffcanvasBody className="hn-offcanvas-body">
          {isLoggedIn && (
            <div className="hn-offcanvas-user">
              <div className="hn-user-summary-name">
                {displayName}
                {userId != null ? ` - ID-${userId}` : ""}
              </div>
              {email && <div className="hn-user-summary-email">{email}</div>}
              <button type="button" className="hn-offcanvas-logout" onClick={handleLogout}>
                Logout
              </button>
            </div>
          )}
          <Nav navbar vertical className="flex-column">
            <NavItem>
              <NavLink tag={Link} to="/" onClick={closeOffcanvas}>
                Home
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink tag={Link} to="/pricing" onClick={closeOffcanvas}>
                Pricing
              </NavLink>
            </NavItem>
            <NavItem>
              <span className="hn-offcanvas-label">Products</span>
              <Nav className="flex-column">
                {navProductLinks()}
              </Nav>
            </NavItem>
            <NavItem>
              {isLoggedIn ? (
                <NavLink tag={Link} to="/dashboard" onClick={closeOffcanvas}>
                  Dashboard
                </NavLink>
              ) : (
                <NavLink tag={Link} to="/login" onClick={closeOffcanvas}>
                  Login
                </NavLink>
              )}
            </NavItem>
          </Nav>
        </OffcanvasBody>
      </Offcanvas>
    </>
  );
};

export default Navbar;
